/**
 * API endpoint constants — single source of truth for every URL in the app.
 *
 * ─ All paths are relative to REACT_APP_BASE_URL (set on the axios instance).
 * ─ Group endpoints by domain (AUTH, DASHBOARD, REPORTS …) as the app grows.
 * ─ Never hard-code a URL string anywhere else — import from here instead.
 */

export const API_ENDPOINTS = {
  // ── Auth ────────────────────────────────────────────────────────────────────
  AUTH: {
    /** POST — email + password → access_token + refresh_token */
    LOGIN: "/auth/login",
    /** POST — email → reset password instructions */
    FORGOT_PASSWORD: "/auth/forgot-password",
    /** POST — reset token + new password → {detail} */
    RESET_PASSWORD: "/auth/reset-password",
    /** POST — refresh_token → new access_token + refresh_token */
    REFRESH: "/auth/refresh",
    /** GET  — Bearer token → current user profile */
    ME: "/auth/me",
    /** POST — invalidate the current session server-side */
    LOGOUT: "/auth/logout",
    /** POST — current_password + new_password → {detail} (own password, self-service) */
    CHANGE_PASSWORD: "/auth/change-password",
  },

  // ── Activity / utilization tracking ─────────────────────────────────────────
  ACTIVITY: {
    /** POST — heartbeat, close beacon, or a batch of client activities. 202, no body. */
    EVENTS: "/activity/events",
  },

  // ── Reports ─────────────────────────────────────────────────────────────────
  REPORTS: {
    /** GET — list available reports */
    LIST: "/reports",
    /** GET — fetch a single report definition/details by code */
    DETAIL: "/reports/:report_code",
    /** POST — run report by code with optional payload/filters */
    RUN: "/reports/:report_code/run",
    /** POST — export report by code with optional payload/filters */
    EXPORT: "/reports/:report_code/export",
  },

  // ── Coverage Outlook (DEV-4558) ─────────────────────────────────────────────
  COVERAGE: {
    /** GET — one case's score, outcome, reasons and model info (case drawer) */
    CASE: "/coverage/cases/:product_sk/:case_id",
    /** GET — the patient's cases in the program, in date order, with what changed */
    PATIENT_TIMELINE: "/coverage/patients/:product_sk/:global_patient_id/timeline",
    /** POST — score a case that isn't in the system yet (nothing is saved) */
    CHECK: "/coverage/check",
  },

  // ── Dashboards ──────────────────────────────────────────────────────────────
  DASHBOARDS: {
    /** GET — list available dashboards; POST — create a dashboard */
    LIST: "/dashboards",
    /** GET — fetch dashboard details; PATCH/DELETE — mutate by dashboard code */
    DETAIL: "/dashboards/:dashboard_code",
    /** POST — fetch dashboard data by dashboard code */
    DATA: "/dashboards/:dashboard_code/data",
    /** PATCH/DELETE — update or clear dashboard layout by dashboard code */
    LAYOUT: "/dashboards/:dashboard_code/layout",
    /** POST — fetch widget data for a dashboard template */
    WIDGET_DATA: "/dashboards/:dashboard_code/widgets/:template_id/data",
    /** POST — download widget data for a dashboard template */
    WIDGET_DATA_DOWNLOAD:
      "/dashboards/:dashboard_code/widgets/:widget_template_id/data/download",
    /** DELETE — remove a widget template from a dashboard */
    WIDGET: "/dashboards/:dashboard_code/widgets/:widget_template_id",
    /** GET — list dashboard templates by dashboard code */
    TEMPLATES: "/widget_templates",
    /** GET — list options for a dashboard filter */
    FILTER_OPTIONS: "/dashboards/:dashboard_code/filters/:filter_key/options",
    /** GET — fetch dashboard dropdown values by type */
    DROPDOWN: "/dashboards/:dashboard_code/dropdowns/:dropdown_type",
    /** GET/POST — list or create dashboard layout patterns */
    LAYOUT_PATTERNS: "/dashboards/layout-patterns",
    /** DELETE — remove a dashboard layout pattern by id */
    LAYOUT_PATTERN_DETAIL: "/dashboards/layout-patterns/:pattern_id",
    /** PATCH — update a dashboard's name */
    UPDATE_DASHBOARD_NAME: "/dashboards/:dashboard_code",
    /** PATCH — set a dashboard as the default */
    SET_DEFAULT: "/dashboards/:dashboard_code/default",
    /** POST — copy a default dashboard into My Dashboards */
    SAVE_AS: "/dashboards/:dashboard_code/save-as",
    /** GET - get soft-deleted dashboards*/
    RECYCLE_BIN: "/dashboards/recycle-bin",
    /** POST - restore a soft-deleted dashboard */
    RESTORE: "/dashboards/:dashboard_code/restore",
    /** DELETE - hard-delete a soft-deleted dashboard; irreversible, 404 if not in the recycle bin */
    PERMANENT_DELETE: "/dashboards/:dashboard_code/permanent",
  },

  // ── Admin (Users, Clients, Programs) ────────────────────────────────────────
  ADMIN: {
    /** GET — list users; POST — create a user */
    USERS: "/admin/users",
    /** GET/PATCH/DELETE — fetch, edit profile fields, or soft-delete a user */
    USER_DETAIL: "/admin/users/:user_id",
    /** PATCH — change a user's role */
    USER_ROLE: "/admin/users/:user_id/role",
    /** PATCH — set a new password for a user */
    USER_PASSWORD: "/admin/users/:user_id/password",
    /** PATCH — full replace of a user's assigned programs */
    USER_PROGRAMS: "/admin/users/:user_id/programs",
    /** PATCH — reactivate a soft-deleted user */
    USER_ACTIVATE: "/admin/users/:user_id/activate",
    /** GET — list companies (clients) the caller can see */
    CLIENTS: "/admin/clients",
    /** GET — fetch a single company by id */
    CLIENT_DETAIL: "/admin/clients/:client_id",
    /** GET — list products (programs); optional ?client_id= (super_admin only) */
    PROGRAMS: "/admin/programs",
    /** GET — fetch a single product by id */
    PROGRAM_DETAIL: "/admin/programs/:program_id",
  },

  // ── Admin portal (DEV-3952) — /portal/* pages, /admin/auth session ──────────
  // Naming note: UI "Programs" are COMPANIES (wire: /admin/clients); UI
  // "Products" are product lines (wire: /admin/programs). Keys speak UI.
  PORTAL: {
    /** POST — email + password → access_token + refresh_token (renewed via AUTH.REFRESH) */
    LOGIN: "/admin/auth/login",
    /** GET — identity + role + tenant context (role gate for the portal) */
    ME: "/admin/auth/me",
    /** GET — list users; POST — create a user */
    USERS: "/admin/users",
    /** GET/PATCH — fetch or edit profile fields; DELETE — soft-delete (deactivate) */
    USER: "/admin/users/:id",
    /** PATCH — change a user's role (super_admin required to grant super_admin) */
    USER_ROLE: "/admin/users/:id/role",
    /** PATCH — full replace of a manager's assigned products */
    USER_PRODUCTS: "/admin/users/:id/programs",
    /** PATCH — move a user to a different program (super_admin only) */
    USER_PROGRAM: "/admin/users/:id/program",
    /** PATCH — set a user's password */
    USER_PASSWORD: "/admin/users/:id/password",
    /** PATCH — reactivate a soft-deleted user */
    USER_ACTIVATE: "/admin/users/:id/activate",
    /** POST — resend the welcome email (resets to the temporary password) */
    USER_RESEND_INVITE: "/admin/users/:id/resend-invite",
    /** GET — role catalog (super_admin / admin / manager) */
    ROLES: "/admin/roles",
    /** GET — headline utilization figures (super_admin only) */
    USAGE_SUMMARY: "/admin/usage/summary",
    /** GET — R1: user, sessions, total time spent */
    USAGE_SESSIONS: "/admin/usage/sessions",
    /** GET — login/logout time of every individual session */
    USAGE_SESSION_LOG: "/admin/usage/session-log",
    /** GET — one session: header, per-dashboard time, full event timeline */
    USAGE_SESSION_DETAIL: "/admin/usage/sessions/:id",
    /** GET — R2: date, user, dashboard, activity */
    USAGE_ACTIVITIES: "/admin/usage/activities",
    /** GET — R3: date, user, dashboard, #activities, time spent (grouped by dashboard) */
    USAGE_DASHBOARDS: "/admin/usage/dashboards",
    /** GET — KPI utilization by hover dwell */
    USAGE_KPIS: "/admin/usage/kpis",
    /** GET — filter picker options, drawn only from data that exists */
    USAGE_FILTERS: "/admin/usage/filters",
    /** GET — every Usage report for the filters as one file (xlsx, all tabs) */
    USAGE_EXPORT: "/admin/usage/export",
    /** GET — list programs (companies); POST — create one (super_admin) */
    PROGRAMS: "/admin/clients",
    /** GET — one program; PATCH — edit name / is_active / attached products */
    PROGRAM: "/admin/clients/:id",
    /** GET — tiers a program licenses; PATCH — full replace (super_admin) */
    PROGRAM_TIERS: "/admin/clients/:id/analytics-tiers",
    /** GET — list products (product lines); POST — create one (super_admin) */
    PRODUCTS: "/admin/programs",
    /** PATCH — edit a product's name / product_sk / is_active (super_admin) */
    PRODUCT: "/admin/programs/:id",
    /** GET — analytics tier catalog; POST — create one (super_admin) */
    TIERS: "/admin/analytics-tiers",
    /** PATCH — edit a tier's name / is_active (super_admin); code/sort_order
     *  are not editable */
    TIER: "/admin/analytics-tiers/:id",
    /** GET — template dashboard catalog (?include_inactive=true); POST —
     *  create one with its tier links (super_admin) */
    DASHBOARDS: "/admin/dashboards",
    /** PATCH — edit name/description/is_active; DELETE — deactivate
     *  (soft, super_admin) */
    DASHBOARD: "/admin/dashboards/:id",
    /** PATCH — full replace of a dashboard's tier links (super_admin) */
    DASHBOARD_TIERS: "/admin/dashboards/:id/tiers",
    /** PATCH — full replace of the clients whose users must NOT see this
     *  template dashboard (super_admin). Body is `{client_ids: [...]}`; the
     *  URL keeps the older "excluded-programs" wording. */
    DASHBOARD_EXCLUDED_PROGRAMS: "/admin/dashboards/:id/excluded-programs",
    /** GET — widget-template catalog (Widget Library), wrapped in
     *  {categories:[{name, templates}]}; flatten client-side. Same catalog
     *  the dashboard builder's own template fetch reads — no /admin prefix,
     *  no elevated role required. */
    WIDGET_TEMPLATES: "/widget_templates",
    /** PATCH — move a widget's Widget Library group and/or its analytics
     *  tier gate (platform super_admin only). */
    WIDGET_TEMPLATE: "/admin/widget-templates/:id",
    /** POST — create a widget template (platform super_admin only); requires
     *  a real, active tier_id — no untiered creation. */
    WIDGET_TEMPLATE_CREATE: "/admin/widget-templates",
  },
} as const;
